import React, { useState, useEffect, useCallback } from 'react';
import {
  adminGetLanguages,
  adminCreateLanguage,
  adminUpdateLanguage,
  adminDeleteLanguage,
} from '../../services/blogApi';
import { BlogLanguage } from '../../types/blog';

interface Props {
  secret: string;
}

const inp: React.CSSProperties = {
  padding: '0.55rem 0.8rem',
  borderRadius: '8px',
  border: '1px solid #e5e7eb',
  fontSize: '0.9rem',
  fontFamily: 'inherit',
  outline: 'none',
};

const AdminLanguages: React.FC<Props> = ({ secret }) => {
  const [languages, setLanguages] = useState<BlogLanguage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // New language form
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [isRtl, setIsRtl] = useState(false);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  // Edit
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editActive, setEditActive] = useState(true);

  const fetchLanguages = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await adminGetLanguages(secret);
      setLanguages(res.data ?? []);
    } catch {
      setError('Failed to load languages.');
    } finally {
      setLoading(false);
    }
  }, [secret]);

  useEffect(() => {
    fetchLanguages();
  }, [fetchLanguages]);

  const handleCreate = async () => {
    if (!code.trim() || !name.trim()) {
      setFormError('Code and name are required.');
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      await adminCreateLanguage(secret, {
        code: code.trim().toLowerCase(),
        name: name.trim(),
        is_rtl: isRtl,
      });
      setCode('');
      setName('');
      setIsRtl(false);
      await fetchLanguages();
    } catch {
      setFormError('Failed to create language.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async (lang: BlogLanguage) => {
    try {
      await adminUpdateLanguage(secret, lang.id, {
        name: editName,
        is_active: editActive,
      });
      setEditingId(null);
      await fetchLanguages();
    } catch {
      alert('Failed to update language.');
    }
  };

  const handleDelete = async (lang: BlogLanguage) => {
    if (!window.confirm(`Delete language "${lang.name}"? This fails if it has published blogs.`)) return;
    try {
      await adminDeleteLanguage(secret, lang.id);
      await fetchLanguages();
    } catch (e: any) {
      alert('Failed to delete. It may have published blogs.');
    }
  };

  const CARD: React.CSSProperties = {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '12px',
    padding: '1.5rem',
    marginBottom: '1.5rem',
    boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
  };

  return (
    <div style={{ maxWidth: '700px', margin: '0 auto' }}>
      {/* Add language */}
      <div style={CARD}>
        <p style={{ fontWeight: 700, fontSize: '0.95rem', color: '#07091A', marginBottom: '1rem' }}>
          Add New Language
        </p>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#6b6b8a', textTransform: 'uppercase', marginBottom: '0.3rem' }}>
              Code
            </label>
            <input
              style={{ ...inp, width: '80px' }}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="en"
              maxLength={5}
            />
          </div>
          <div style={{ flex: 1, minWidth: '160px' }}>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#6b6b8a', textTransform: 'uppercase', marginBottom: '0.3rem' }}>
              Name
            </label>
            <input
              style={{ ...inp, width: '100%', boxSizing: 'border-box' }}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="English"
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', paddingBottom: '0.15rem' }}>
            <input
              type="checkbox"
              id="rtl"
              checked={isRtl}
              onChange={(e) => setIsRtl(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            <label htmlFor="rtl" style={{ fontSize: '0.85rem', cursor: 'pointer', color: '#07091A' }}>RTL</label>
          </div>
          <button
            onClick={handleCreate}
            disabled={saving}
            style={{
              padding: '0.55rem 1.25rem',
              background: 'linear-gradient(135deg, #2DD4BF 0%, #4C82FF 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontWeight: 700,
              cursor: saving ? 'not-allowed' : 'pointer',
              fontSize: '0.9rem',
              fontFamily: 'inherit',
              opacity: saving ? 0.7 : 1,
            }}
          >
            {saving ? 'Adding...' : 'Add'}
          </button>
        </div>
        {formError && (
          <p style={{ color: '#ef4444', fontSize: '0.85rem', marginTop: '0.5rem' }}>{formError}</p>
        )}
      </div>

      {/* Languages table */}
      <div style={CARD}>
        <p style={{ fontWeight: 700, fontSize: '0.95rem', color: '#07091A', marginBottom: '1rem' }}>
          Languages ({languages.length})
        </p>

        {loading && <p style={{ color: '#6b6b8a', fontSize: '0.9rem' }}>Loading...</p>}
        {!loading && error && <p style={{ color: '#ef4444', fontSize: '0.9rem' }}>{error}</p>}

        {!loading && !error && (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Code', 'Name', 'RTL', 'Active', 'Actions'].map((h) => (
                  <th key={h} style={{ padding: '0.6rem 0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 700, color: '#6b6b8a', textTransform: 'uppercase', borderBottom: '1px solid #e5e7eb' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {languages.map((lang) => (
                <tr key={lang.id}>
                  <td style={{ padding: '0.75rem', fontWeight: 700, fontSize: '0.85rem', color: '#07091A', borderBottom: '1px solid #f3f4f6' }}>
                    {lang.code.toUpperCase()}
                  </td>
                  <td style={{ padding: '0.75rem', fontSize: '0.9rem', borderBottom: '1px solid #f3f4f6' }}>
                    {editingId === lang.id ? (
                      <input
                        style={{ ...inp, width: '140px' }}
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                      />
                    ) : (
                      lang.name
                    )}
                  </td>
                  <td style={{ padding: '0.75rem', fontSize: '0.85rem', borderBottom: '1px solid #f3f4f6' }}>
                    {lang.is_rtl ? '✓' : '—'}
                  </td>
                  <td style={{ padding: '0.75rem', fontSize: '0.85rem', borderBottom: '1px solid #f3f4f6' }}>
                    {editingId === lang.id ? (
                      <input
                        type="checkbox"
                        checked={editActive}
                        onChange={(e) => setEditActive(e.target.checked)}
                      />
                    ) : (
                      <span style={{ color: lang.is_active !== false ? '#22c55e' : '#9ca3af' }}>
                        {lang.is_active !== false ? 'Active' : 'Inactive'}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '0.75rem', borderBottom: '1px solid #f3f4f6' }}>
                    {editingId === lang.id ? (
                      <div style={{ display: 'flex', gap: '0.35rem' }}>
                        <button onClick={() => handleSaveEdit(lang)} style={{ padding: '0.25rem 0.6rem', borderRadius: '6px', border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: '0.8rem', fontFamily: 'inherit', color: '#22c55e' }}>Save</button>
                        <button onClick={() => setEditingId(null)} style={{ padding: '0.25rem 0.6rem', borderRadius: '6px', border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: '0.8rem', fontFamily: 'inherit' }}>Cancel</button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: '0.35rem' }}>
                        <button onClick={() => { setEditingId(lang.id); setEditName(lang.name); setEditActive(lang.is_active !== false); }} style={{ padding: '0.25rem 0.6rem', borderRadius: '6px', border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: '0.8rem', fontFamily: 'inherit' }}>Edit</button>
                        <button onClick={() => handleDelete(lang)} style={{ padding: '0.25rem 0.6rem', borderRadius: '6px', border: '1px solid #fee2e2', background: '#fff', cursor: 'pointer', fontSize: '0.8rem', fontFamily: 'inherit', color: '#ef4444' }}>Delete</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {languages.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af', fontSize: '0.9rem' }}>
                    No languages yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AdminLanguages;
